import React, { useState, useCallback, useEffect, useRef } from 'react';
import { authService } from '../services/authService';
import StepClientSearch from './incidence-steps/StepClientSearch';
import Step3ProductData from './incidence-steps/Step3ProductData';
import Step4IncidenceDetails from './incidence-steps/Step4IncidenceDetails';
import AppointmentSection from './incidence-steps/AppointmentSection';
import PrecioEstimadoMTO from './incidence-steps/PrecioEstimadoMTO';
import { trackEvent } from '../lib/analytics';

// ── Estado inicial ─────────────────────────────────────────────────────────────
const INITIAL_FORM_DATA = {
  telefono: '', nombreCompleto: '', email: '', direccion: '',
  codigoPostal: '', localidad: '', provincia: '',
  clienteVerificado: false, clienteId: null, addressId: null,
  tipoIncidencia: '', tipoIncidenciaTitulo: '', tipoIncidenciaEmoji: '',
  assetId: null, marca: '', modelo: '', tipoEquipo: '', numeroSerie: '',
  fechaInstalacion: '', instaladoPor: '', otroInstalador: '',
  descripcionFallo: '', fechaProblema: '', codigoError: '',
  urgenciaProblema: '', esGarantia: '', marcaGarantia: '',
  archivosAdjuntos: [], tipoInstalacion: '', urgenciaInstalacion: '',
  interesadoContrato: '',
  tipoEquipoMantenimiento: '', litrajeTermoMTO: '', splitUnidades: '', splitIncluirExterior: '',
  numeroUnidades: '', ubicacionInteriores: '',
  ubicacionExterior: '', ultimoMantenimiento: '', problemasDetectados: '',
  observacionesMantenimiento: '', servicioPresupuesto: '',
  tipoViviendaPresupuesto: '', metrosPresupuesto: '', equipoActualPresupuesto: '',
  decisionMakerPresupuesto: '', comoConocioPresupuesto: '',
  urgenciaPresupuesto: '', detallesPresupuesto: '', tipoGarantia: '', tipoGarantiaItemId: null,
  tieneFactura: '', facturaAdjunta: null,
  aceptaCondiciones: false, garantiaEstandarConfirmada: false,
  citaSeleccionada: null, incidenciaId: null, notasOperador: '',
};

const CLEAR_ON_TYPE_CHANGE = {
  descripcionFallo: '', fechaProblema: '', codigoError: '',
  urgenciaProblema: '', esGarantia: '', marcaGarantia: '',
  archivosAdjuntos: [], tipoGarantia: '', tipoGarantiaItemId: null,
  tieneFactura: '', facturaAdjunta: null,
  aceptaCondiciones: false, garantiaEstandarConfirmada: false,
  interesadoContrato: '',
  tipoEquipoMantenimiento: '', litrajeTermoMTO: '', splitUnidades: '', splitIncluirExterior: '',
  ultimoMantenimiento: '',
  problemasDetectados: '', observacionesMantenimiento: '',
  tipoInstalacion: '', urgenciaInstalacion: '',
  servicioPresupuesto: '', tipoViviendaPresupuesto: '', metrosPresupuesto: '',
  equipoActualPresupuesto: '', decisionMakerPresupuesto: '', comoConocioPresupuesto: '',
  urgenciaPresupuesto: '', detallesPresupuesto: '',
  citaSeleccionada: null, incidenciaId: null, notasOperador: '',
};

// ── Tipos de servicio ──────────────────────────────────────────────────────────
const TIPOS_SERVICIO = [
  { id: 'sat',           emoji: '🔧', label: 'SAT',           desc: 'Reparación / avería',    cls: { off: 'border-gray-200 hover:border-red-300 hover:bg-red-50',     on: 'border-red-500 bg-red-50 text-red-800' } },
  { id: 'mantenimiento', emoji: '🔄', label: 'Mantenimiento', desc: 'Revisión anual',          cls: { off: 'border-gray-200 hover:border-blue-300 hover:bg-blue-50',   on: 'border-blue-500 bg-blue-50 text-blue-800' } },
  { id: 'presupuesto',   emoji: '💶', label: 'Presupuesto',   desc: 'Solicitar valoración',   cls: { off: 'border-gray-200 hover:border-violet-300 hover:bg-violet-50', on: 'border-violet-500 bg-violet-50 text-violet-800' } },
];

// ── Helpers ────────────────────────────────────────────────────────────────────
function formatTimer(s) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m ${r < 10 ? '0' : ''}${r}s` : `${s}s`;
}

function isScriptComplete(fd) {
  switch (fd.tipoIncidencia) {
    case 'sat':
      return !!(fd.descripcionFallo?.length >= 10 &&
        fd.fechaProblema && fd.urgenciaProblema && fd.esGarantia &&
        (fd.esGarantia === 'no' || (fd.marcaGarantia && fd.tieneFactura)));
    case 'mantenimiento':
      if (fd.interesadoContrato === 'si') return true; // redirige, no necesita más
      if (fd.interesadoContrato === 'no') {
        if (!fd.tipoEquipoMantenimiento || !fd.ultimoMantenimiento) return false;
        const needsSplit = ['split', 'aerotermo_split'].includes(fd.tipoEquipoMantenimiento);
        if (needsSplit && (!fd.splitUnidades || !fd.splitIncluirExterior)) return false;
        return true;
      }
      return false;
    case 'presupuesto':    return !!(fd.servicioPresupuesto && fd.tipoViviendaPresupuesto && fd.urgenciaPresupuesto);
    default:               return false;
  }
}

// ── Selector de tipo de servicio (inline) ─────────────────────────────────────
const ServiceTypePicker = ({ value, onChange }) => (
  <div className="grid grid-cols-3 gap-2 p-4">
    {TIPOS_SERVICIO.map(t => {
      const on = value === t.id;
      return (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          className={`flex flex-col items-center gap-1.5 px-3 py-3.5 rounded-xl border-2 transition-all text-center ${on ? t.cls.on : `bg-white text-gray-600 ${t.cls.off}`}`}
        >
          <span className="text-2xl leading-none">{t.emoji}</span>
          <p className="text-sm font-bold leading-tight">{t.label}</p>
          <p className="text-xs opacity-60 leading-tight">{t.desc}</p>
          {on && <span className="text-xs font-bold mt-0.5">✓</span>}
        </button>
      );
    })}
  </div>
);

// ── Bloque de sección visual ──────────────────────────────────────────────────
const SectionBlock = ({ title, icon, done, fadeIn, children }) => (
  <div className={`bg-white rounded-2xl border overflow-hidden shadow-sm ${done ? 'border-emerald-200' : 'border-gray-200'} ${fadeIn ? 'animate-fade-in' : ''}`}>
    <div className={`flex items-center gap-2.5 px-4 py-3 border-b ${done ? 'bg-emerald-50/60 border-emerald-100' : 'bg-gray-50/60 border-gray-100'}`}>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${done ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
        {done ? '✓' : icon}
      </div>
      <span className={`font-bold text-sm ${done ? 'text-emerald-800' : 'text-gray-700'}`}>{title}</span>
    </div>
    {children}
  </div>
);

// ── Pantalla de éxito ─────────────────────────────────────────────────────────
const SuccessScreen = ({ resultado, formData, timerSeconds, onReset }) => {
  const [copiado, setCopiado] = useState(false);
  const ref =
    resultado?.workorder?.fullReference ||
    resultado?.incident?.fullReference  ||
    resultado?.workorder?.reference     ||
    resultado?.incident?.reference      || '';
  const tipoDoc = resultado?.documentType === 'workorder' ? 'Orden de trabajo' : 'Solicitud';
  const cita    = formData.citaSeleccionada;

  const filas = [
    { icon: '👤', label: 'Cliente',   value: formData.nombreCompleto },
    { icon: '📞', label: 'Teléfono',  value: formData.telefono },
    formData.email && { icon: '✉️',  label: 'Email',    value: formData.email },
    { icon: '📍', label: 'Dirección', value: [formData.direccion, formData.localidad].filter(Boolean).join(', ') },
    { icon: formData.tipoIncidenciaEmoji || '🎯', label: 'Servicio', value: formData.tipoIncidenciaTitulo },
    formData.marca && { icon: '⚙️', label: 'Equipo', value: `${formData.marca}${formData.tipoEquipo ? ' · ' + formData.tipoEquipo : ''}` },
    cita && { icon: '📅', label: 'Cita',    value: cita.dia },
    cita && { icon: '👷', label: 'Técnico', value: cita.tecnico },
    { icon: '⏱', label: 'Tiempo',    value: formatTimer(timerSeconds) },
  ].filter(Boolean);

  return (
    <div className="min-h-screen flex flex-col items-center py-12 px-4" style={{ backgroundColor: '#f1f5f9' }}>
      <div className="w-full max-w-md">

        <div className="text-center mb-6">
          <div className="inline-flex w-16 h-16 rounded-2xl bg-emerald-100 items-center justify-center mb-4">
            <svg className="w-9 h-9 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900">{tipoDoc} creada</h2>
          <p className="text-sm text-gray-400 mt-1">Registrada en StelOrder</p>
        </div>

        {ref && (
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl px-6 py-4 flex items-center justify-between gap-4 mb-4">
            <div>
              <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-1">Referencia</p>
              <p className="font-mono text-2xl font-bold text-emerald-700 tracking-widest">{ref}</p>
            </div>
            <button
              onClick={() => navigator.clipboard.writeText(ref).then(() => { setCopiado(true); setTimeout(() => setCopiado(false), 2000); })}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all flex-shrink-0 ${
                copiado ? 'bg-emerald-600 text-white' : 'bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-100'
              }`}
            >
              {copiado ? '✓ Copiado' : '📋 Copiar'}
            </button>
          </div>
        )}

        <div className="bg-white rounded-2xl border overflow-hidden shadow-sm mb-5">
          <div className="bg-gray-50 border-b px-4 py-2.5">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Resumen</span>
          </div>
          <div className="divide-y divide-gray-50">
            {filas.map((f, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-2.5 gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm flex-shrink-0">{f.icon}</span>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex-shrink-0">{f.label}</span>
                </div>
                <span className="text-sm font-semibold text-gray-800 text-right truncate max-w-[55%]">{f.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onReset}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-sm transition-colors"
            style={{ boxShadow: '0 2px 8px rgba(220,38,38,0.25)' }}
          >
            ➕ Nueva solicitud
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl font-semibold text-sm border border-gray-200 transition-colors"
          >
            🖨️ <span className="hidden sm:inline">Imprimir</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Componente principal ───────────────────────────────────────────────────────
const IncidenceFormView = ({ onNavigate }) => {
  const [formData, setFormData] = useState(() => {
    try {
      const prefill = sessionStorage.getItem('zapata_crm_prefill');
      if (prefill) {
        sessionStorage.removeItem('zapata_crm_prefill');
        sessionStorage.removeItem('zapata_crm_draft');
        return { ...INITIAL_FORM_DATA, ...JSON.parse(prefill) };
      }
    } catch {}
    return INITIAL_FORM_DATA;
  });
  const [enviando,   setEnviando]   = useState(false);
  const [enviado,    setEnviado]    = useState(false);
  const [resultado,  setResultado]  = useState(null);
  const [errorEnvio, setErrorEnvio] = useState('');
  const [sinCitaMode, setSinCitaMode] = useState(false);
  const [showNoFacturaModal, setShowNoFacturaModal] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const timerRef     = useRef(null);
  const timerStarted = useRef(false);

  // ── Estado derivado ──────────────────────────────────────────────────────────
  const clientVerified   = !!formData.addressId;
  const serviceSelected  = !!formData.tipoIncidencia;
  const needsEquipment      = ['sat', 'mantenimiento'].includes(formData.tipoIncidencia);
  // Para mantenimiento, equipo y cita solo aparecen después de confirmar que NO quiere contrato
  const mantenimientoPuntual = formData.tipoIncidencia !== 'mantenimiento' || formData.interesadoContrato === 'no';
  const equipmentDone    = !needsEquipment || !!(formData.marca && formData.modelo && formData.tipoEquipo);
  const scriptDone       = isScriptComplete(formData);
  const needsAppointment = needsEquipment;
  const appointmentDone  = !needsAppointment || sinCitaMode || !!formData.citaSeleccionada;
  const canSubmit        = clientVerified && serviceSelected && equipmentDone && scriptDone && appointmentDone;

  // ── Timer ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (serviceSelected && !timerStarted.current) {
      timerStarted.current = true;
      timerRef.current = setInterval(() => setTimerSeconds(s => s + 1), 1000);
    }
  }, [serviceSelected]);

  useEffect(() => () => clearInterval(timerRef.current), []);

  // ── Redirección a Mantenimientos si el cliente quiere contrato ───────────────
  useEffect(() => {
    if (formData.tipoIncidencia === 'mantenimiento' && formData.interesadoContrato === 'si' && onNavigate) {
      const timer = setTimeout(() => {
        // Limpiar draft para evitar bucle al volver al CRM
        sessionStorage.removeItem('zapata_crm_draft');
        onNavigate('mantenimientos', {
          clienteId:          formData.clienteId,
          clienteNombre:      formData.nombreCompleto,
          clienteTelefono:    formData.telefono,
          clienteEmail:       formData.email,
          direccion:          formData.direccion,
          municipio:          formData.localidad,
          marca:              formData.marca,
          modelo:             formData.modelo,
          numeroSerie:        formData.numeroSerie,
          tipoEquipo:         formData.tipoEquipo,
          fechaInstalacion:   formData.fechaInstalacion,
        });
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [formData.interesadoContrato]); // eslint-disable-line

  // ── Autosave ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (serviceSelected && !enviado) {
      sessionStorage.setItem('zapata_crm_draft', JSON.stringify(formData));
    } else if (enviado) {
      sessionStorage.removeItem('zapata_crm_draft');
    }
  }, [formData, serviceSelected, enviado]);

  // ── Restaurar borrador ───────────────────────────────────────────────────────
  useEffect(() => {
    try {
      const draft = sessionStorage.getItem('zapata_crm_draft');
      if (draft) setFormData(f => ({ ...f, ...JSON.parse(draft) }));
    } catch {}
  }, []); // eslint-disable-line

  // ── Acciones ─────────────────────────────────────────────────────────────────
  const updateFormData = useCallback((data) => setFormData(p => ({ ...p, ...data })), []);

  const handleReset = useCallback(() => {
    clearInterval(timerRef.current);
    setFormData(INITIAL_FORM_DATA);
    setEnviado(false);
    setResultado(null);
    setErrorEnvio('');
    setSinCitaMode(false);
    setTimerSeconds(0);
    timerStarted.current = false;
    sessionStorage.removeItem('zapata_crm_draft');
  }, []);

  // Reset manual a mitad de formulario — el autosave (zapata_crm_draft) deja
  // lo escrito guardado aunque se cierre pestaña/se navegue a otra parte del
  // CRM, para no perder trabajo si fue sin querer. Pero eso significa que si
  // el operador SÍ quiere abandonar a propósito (cliente equivocado, llamada
  // de prueba, se lo piensa mejor) necesita una forma explícita de vaciarlo
  // todo de golpe — los botones "Cambiar" de Cliente/Tipo de servicio solo
  // limpian su propia sección, no el equipo/detalles ya rellenos debajo.
  const handleResetManual = useCallback(() => {
    if (window.confirm('¿Vaciar todo lo introducido y empezar de nuevo? Se perderá lo que llevas escrito.')) {
      handleReset();
    }
  }, [handleReset]);

  const handleServiceChange = useCallback((tipoId) => {
    const tipo = TIPOS_SERVICIO.find(t => t.id === tipoId);
    updateFormData({
      tipoIncidencia:       tipoId,
      tipoIncidenciaTitulo: tipo.label,
      tipoIncidenciaEmoji:  tipo.emoji,
      ...CLEAR_ON_TYPE_CHANGE,
    });
    setSinCitaMode(false);
  }, [updateFormData]);

  const handleClearService = useCallback(() => {
    updateFormData({ tipoIncidencia: '', tipoIncidenciaTitulo: '', tipoIncidenciaEmoji: '', ...CLEAR_ON_TYPE_CHANGE });
    setSinCitaMode(false);
  }, [updateFormData]);

  // ── Gate: garantía sin factura ────────────────────────────────────────────────
  const handleCrearOrden = () => {
    if (formData.esGarantia === 'si' && formData.tieneFactura === 'no') {
      setShowNoFacturaModal(true);
      return;
    }
    crearOrden();
  };

  // ── Crear orden (unificado para todos los tipos) ──────────────────────────────
  const crearOrden = async (overrides = {}) => {
    setShowNoFacturaModal(false);
    setEnviando(true);
    setErrorEnvio('');
    const timerLabel = `\n\n⏱ Tiempo de gestión: ${formatTimer(timerSeconds)}`;

    try {
      const d = { ...formData, ...overrides };
      const datosParaEnviar = {
        // Cliente
        telefono: d.telefono, nombreCompleto: d.nombreCompleto,
        email: d.email, direccion: d.direccion,
        codigoPostal: d.codigoPostal, localidad: d.localidad,
        provincia: d.provincia, clienteVerificado: d.clienteVerificado,
        clienteId: d.clienteId, addressId: d.addressId,
        // Equipo
        assetId: d.assetId, marca: d.marca, modelo: d.modelo,
        tipoEquipo: d.tipoEquipo, numeroSerie: d.numeroSerie,
        fechaInstalacion: d.fechaInstalacion, instaladoPor: d.instaladoPor,
        // Tipo
        tipoIncidencia: d.tipoIncidencia,
        // SAT
        descripcionFallo: d.tipoIncidencia === 'sat'
          ? (d.descripcionFallo || '') + timerLabel
          : d.descripcionFallo,
        fechaProblema: d.fechaProblema, codigoError: d.codigoError,
        urgenciaProblema: d.urgenciaProblema,
        esGarantia: d.esGarantia, marcaGarantia: d.marcaGarantia,
        tieneFactura: d.tieneFactura, facturaAdjunta: d.facturaAdjunta,
        tipoGarantia: d.tipoGarantia, tipoGarantiaItemId: d.tipoGarantiaItemId,
        archivosAdjuntos: d.archivosAdjuntos,
        // Mantenimiento
        tipoEquipoMantenimiento: d.tipoEquipoMantenimiento,
        litrajeTermoMTO: d.litrajeTermoMTO,
        splitUnidades: d.splitUnidades, splitIncluirExterior: d.splitIncluirExterior,
        numeroUnidades: d.numeroUnidades, marcaModelo: d.marcaModelo,
        ubicacionInteriores: d.ubicacionInteriores, ubicacionExterior: d.ubicacionExterior,
        ultimoMantenimiento: d.ultimoMantenimiento,
        problemasDetectados: d.problemasDetectados,
        detalleProblemas: d.detalleProblemas, tipoLimpieza: d.tipoLimpieza,
        rejillasAccesibles: d.rejillasAccesibles, ubicacionInterior: d.ubicacionInterior,
        filtrosAccesibles: d.filtrosAccesibles, controlZonas: d.controlZonas,
        limpiezaCompleta: d.limpiezaCompleta,
        modeloCapacidad: d.modeloCapacidad, ubicacionAerotermo: d.ubicacionAerotermo,
        problemasRendimiento: d.problemasRendimiento,
        detalleProblemasRendimiento: d.detalleProblemasRendimiento,
        desagueAccesible: d.desagueAccesible, espacioLibre: d.espacioLibre,
        limpiezaEvaporador: d.limpiezaEvaporador,
        capacidadTermo: d.capacidadTermo, ubicacionTermo: d.ubicacionTermo,
        problemasFuncionamiento: d.problemasFuncionamiento,
        detalleProblemasFuncionamiento: d.detalleProblemasFuncionamiento,
        valvulaSeguridad: d.valvulaSeguridad, espacioAcceso: d.espacioAcceso,
        sustitucionAnodo: d.sustitucionAnodo,
        observacionesMantenimiento: d.tipoIncidencia === 'mantenimiento'
          ? (d.observacionesMantenimiento || '') + timerLabel
          : d.observacionesMantenimiento,
        accesoFacilVivienda: d.accesoFacilVivienda,
        viviendaHabitada: d.viviendaHabitada,
        canalComunicacion: d.canalComunicacion,
        // Instalación
        tipoInstalacion: d.tipoInstalacion,
        preinstalacion: d.preinstalacion, superficieAprox: d.superficieAprox,
        marcaPreferida: d.marcaPreferida, observacionesLibres: d.observacionesLibres,
        urgenciaInstalacion: d.urgenciaInstalacion,
        // Presupuesto
        servicioPresupuesto: d.servicioPresupuesto,
        tipoViviendaPresupuesto: d.tipoViviendaPresupuesto,
        metrosPresupuesto: d.metrosPresupuesto,
        equipoActualPresupuesto: d.equipoActualPresupuesto,
        decisionMakerPresupuesto: d.decisionMakerPresupuesto,
        comoConocioPresupuesto: d.comoConocioPresupuesto,
        urgenciaPresupuesto: d.urgenciaPresupuesto,
        detallesPresupuesto: d.tipoIncidencia === 'presupuesto'
          ? (d.detallesPresupuesto || '') + timerLabel
          : d.detallesPresupuesto,
        // Comunes
        notasOperador: d.notasOperador,
        citaSeleccionada: d.citaSeleccionada,
        sinCita: sinCitaMode,
      };

      const endpoint = needsEquipment && d.citaSeleccionada
        ? '/.netlify/functions/createWorkOrder'
        : '/.netlify/functions/createIncident';

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify(datosParaEnviar),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error || `Error HTTP ${res.status}`);
      }
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Error desconocido');

      if (endpoint === '/.netlify/functions/createIncident')
        trackEvent('incidencia_creada', { tipoIncidencia: d.tipoIncidencia || null });

      clearInterval(timerRef.current);
      setResultado(data);
      setEnviado(true);
    } catch (err) {
      setErrorEnvio(err.message);
    } finally {
      setEnviando(false);
    }
  };

  // ── Pantalla de éxito ─────────────────────────────────────────────────────────
  if (enviado && resultado) {
    return (
      <SuccessScreen
        resultado={resultado}
        formData={formData}
        timerSeconds={timerSeconds}
        onReset={handleReset}
      />
    );
  }

  // ── Formulario ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen pb-28" style={{ backgroundColor: '#f1f5f9' }}>
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-3">

        {/* ── Vaciar formulario a medias ── */}
        {(clientVerified || serviceSelected) && (
          <div className="flex justify-end -mb-1">
            <button
              type="button"
              onClick={handleResetManual}
              className="text-xs text-gray-400 hover:text-red-600 font-semibold flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
            >
              🗑️ Vaciar y empezar de nuevo
            </button>
          </div>
        )}

        {/* ── 1. Cliente ── */}
        <SectionBlock title="Cliente" icon="👤" done={clientVerified}>
          {clientVerified ? (
            <div className="flex items-center justify-between px-4 py-3 gap-3">
              <div className="min-w-0">
                <p className="text-sm font-bold text-gray-800 truncate">{formData.nombreCompleto}</p>
                <p className="text-xs text-gray-500 mt-0.5 truncate">
                  {formData.telefono}{formData.localidad ? ` · ${formData.localidad}` : ''}
                </p>
              </div>
              <button
                onClick={() => updateFormData({
                  clienteVerificado: false, addressId: null, clienteId: null,
                  nombreCompleto: '', telefono: '', email: '',
                  direccion: '', codigoPostal: '', localidad: '', provincia: '',
                })}
                className="text-xs text-red-500 hover:text-red-700 font-semibold flex-shrink-0 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
              >
                Cambiar
              </button>
            </div>
          ) : (
            <StepClientSearch
              formData={formData}
              updateFormData={updateFormData}
              goToNextStep={() => {}}
            />
          )}
        </SectionBlock>

        {/* ── 2. Tipo de servicio ── */}
        {clientVerified && (
          <SectionBlock title="Tipo de servicio" icon="🎯" done={serviceSelected} fadeIn>
            {serviceSelected ? (
              <div className="flex items-center justify-between px-4 py-3">
                <p className="text-sm font-bold text-gray-800">
                  {formData.tipoIncidenciaEmoji} {formData.tipoIncidenciaTitulo}
                </p>
                <button
                  onClick={handleClearService}
                  className="text-xs text-red-500 hover:text-red-700 font-semibold px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
                >
                  Cambiar
                </button>
              </div>
            ) : (
              <ServiceTypePicker value={formData.tipoIncidencia} onChange={handleServiceChange} />
            )}
          </SectionBlock>
        )}

        {/* ── Secciones post-servicio ── */}
        {serviceSelected && (
          <>
            {/* Banner de redirección a contrato */}
            {formData.interesadoContrato === 'si' && (
              <div className="flex items-center gap-3 bg-emerald-50 border-2 border-emerald-200 rounded-2xl px-5 py-4 animate-fade-in">
                <div className="w-5 h-5 border-2 border-emerald-300 border-t-emerald-600 rounded-full animate-spin flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-emerald-800">Abriendo formulario de contrato…</p>
                  <p className="text-xs text-emerald-600 mt-0.5">Redirigiendo a Mantenimientos con los datos del cliente</p>
                </div>
              </div>
            )}

            {/* ── 3. Equipo (SAT / Mantenimiento puntual) ── */}
            {needsEquipment && mantenimientoPuntual && (
              <SectionBlock title="Equipo" icon="⚙️" done={equipmentDone} fadeIn>
                <Step3ProductData formData={formData} updateFormData={updateFormData} />
              </SectionBlock>
            )}

            {/* ── 4. Detalles (script CRM) ── */}
            {formData.interesadoContrato !== 'si' && (
              <SectionBlock title="Detalles" icon="📋" done={scriptDone} fadeIn>
                <Step4IncidenceDetails formData={formData} updateFormData={updateFormData} />
              </SectionBlock>
            )}

            {/* ── Precio estimado mantenimiento ── */}
            {formData.tipoIncidencia === 'mantenimiento' && mantenimientoPuntual && formData.tipoEquipoMantenimiento && (
              <PrecioEstimadoMTO formData={formData} updateFormData={updateFormData} />
            )}

            {/* ── 5. Cita (SAT / Mantenimiento puntual ya confirmado) ── */}
            {needsAppointment && mantenimientoPuntual && (
              <SectionBlock title="Cita" icon="📅" done={appointmentDone} fadeIn>
                <AppointmentSection
                  formData={formData}
                  updateFormData={updateFormData}
                  sinCitaMode={sinCitaMode}
                  onSinCitaToggle={setSinCitaMode}
                />
              </SectionBlock>
            )}
          </>
        )}

      </div>

      {/* ── Barra inferior fija ──────────────────────────────────────────────── */}
      {serviceSelected && (
        <div
          className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200"
          style={{ boxShadow: '0 -2px 12px rgba(0,0,0,0.07)' }}
        >
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">

            {/* Timer */}
            <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 flex-shrink-0">
              <span className="text-xs">⏱</span>
              <span className="font-mono text-xs font-bold text-amber-700">{formatTimer(timerSeconds)}</span>
            </div>

            {/* Error */}
            {errorEnvio ? (
              <div className="flex-1 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-700 flex items-center gap-2 min-w-0">
                <span className="flex-1 truncate">{errorEnvio}</span>
                <button onClick={() => setErrorEnvio('')} className="text-red-400 hover:text-red-600 flex-shrink-0 text-base leading-none">✕</button>
              </div>
            ) : (
              <div className="flex-1" />
            )}

            {/* Botón crear */}
            <button
              onClick={handleCrearOrden}
              disabled={!canSubmit || enviando}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all flex-shrink-0 text-white ${
                canSubmit && !enviando
                  ? 'bg-emerald-600 hover:bg-emerald-700'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
              style={{ boxShadow: canSubmit && !enviando ? '0 2px 8px rgba(5,150,105,0.35)' : 'none' }}
            >
              {enviando ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creando…
                </>
              ) : (
                <>📤 Crear orden</>
              )}
            </button>

          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.25s ease-out; }
      `}</style>

      {/* ── Modal: garantía sin factura ─────────────────────────────────────── */}
      {showNoFacturaModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm px-4 pb-4 sm:pb-0">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden">

            {/* Cabecera */}
            <div className="bg-amber-50 border-b border-amber-200 px-6 py-4 flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-amber-100 border-2 border-amber-300 flex items-center justify-center flex-shrink-0 text-xl">
                ⚠️
              </div>
              <div>
                <h3 className="font-bold text-gray-900 leading-tight">No se puede tramitar en garantía</h3>
                <p className="text-sm text-amber-800 mt-1 leading-snug">
                  El cliente <strong>no dispone de factura de compra</strong>. Sin ella el fabricante no acepta la solicitud de garantía.
                </p>
              </div>
            </div>

            {/* Cuerpo */}
            <div className="px-6 py-4 text-sm text-gray-600 leading-relaxed">
              Informa al cliente que la reparación tendrá coste, o consulta con un responsable antes de continuar.
            </div>

            {/* Acciones */}
            <div className="px-6 pb-5 space-y-2.5">
              <button
                type="button"
                onClick={() => crearOrden({ esGarantia: 'no', marcaGarantia: '', tipoGarantia: '', tipoGarantiaItemId: null })}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm transition-colors"
                style={{ boxShadow: '0 2px 8px rgba(37,99,235,0.3)' }}
              >
                💰 Crear como reparación con coste
              </button>
              <button
                type="button"
                onClick={() => crearOrden()}
                className="w-full py-3 border-2 border-amber-300 bg-amber-50 text-amber-800 rounded-xl font-semibold text-sm hover:bg-amber-100 transition-colors"
              >
                ⚠️ Continuar bajo mi responsabilidad
              </button>
              <button
                type="button"
                onClick={() => setShowNoFacturaModal(false)}
                className="w-full py-2 text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                Cancelar — volver al formulario
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncidenceFormView;
